import ShutterImage from '@/shared/assets/images/shutter.png';
import { Button, Input } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { createPortal } from 'react-dom';
import { styles } from './styles';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@shared/hooks/useToast';
import { useGenerateFormLinks, exportLinksToExcel, type FormLink } from '@features/leadCollector';
import { FormStatusEnum } from '@entities/leadCollector';
import { XmarkIcon } from '@shared/assets/icons';

const LINKS_ALREADY_GENERATED_ERROR = 'Links already generated';

interface CreateLinksModalProps {
  active?: boolean;
  onCreateLinks: () => void;
  onClose?: () => void;
  refetchEvent?: () => void;
}

export const CreateLinksModal = ({ active, onCreateLinks, onClose, refetchEvent }: CreateLinksModalProps) => {
  const { id } = useParams<{ id: string }>();
  const [countLinks, setCountLinks] = useState('');
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const { generateFormLinks } = useGenerateFormLinks();

  if (!active) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  const handleCreateClick = async () => {
    if (!countLinks) return setError('Обязательное поле');
    setError('');

    const count = Number(countLinks);
    const eventId = Number(id);

    const result = await generateFormLinks({ eventId, count });

    if (result.error?.message?.includes(LINKS_ALREADY_GENERATED_ERROR)) {
      showToast('Ошибка загрузки данных: ссылки уже сформированы', 'error');
      return;
    }

    if (result.error) {
      showToast('Ошибка загрузки данных', 'error');
      return;
    }

    const links = result.data?.generateFormLinks;
    if (!links?.length) return;

    const formLinks: FormLink[] = [];
    for (const link of links) {
      if (link.linkToken) {
        formLinks.push({ linkToken: link.linkToken, status: FormStatusEnum.ACTIVE });
      }
    }

    if (!formLinks.length) return;

    await exportLinksToExcel(formLinks, true);
    refetchEvent?.();

    onCreateLinks();
  };

  const handleCountLinksChange = (value: string) => {
    setCountLinks(value);
    error && setError('');
  };

  return createPortal(
    <div {...stylex.props(styles.overlay)} onClick={handleOverlayClick} onKeyDown={handleOverlayKeyDown} role="dialog">
      <div {...stylex.props(styles.modalContainer)} role="dialog" onMouseDown={(e) => e.stopPropagation()}>
        <button {...stylex.props(styles.closeButton)} onClick={onClose}>
          <XmarkIcon size="24" {...stylex.props(styles.closeIcon)} />
        </button>
        <div {...stylex.props(styles.container)}>
          <img src={ShutterImage} height={140} alt="Сколько ссылок необходимо создать" />

          <div {...stylex.props(styles.description)}>
            <p {...stylex.props(styles.title)}>Сколько ссылок необходимо создать?</p>
            <p {...stylex.props(styles.subTitle)}>Введите необходимое количество уникальных ссылок</p>
          </div>

          <div {...stylex.props(styles.actions)}>
            <div {...stylex.props(styles.inputWrapper)}>
              <Input
                value={countLinks}
                changeFunction={handleCountLinksChange}
                type="number"
                size="small"
                placeholder="Количество"
                settings={{ maxWidth: '132px', height: '36px' }}
                status={error ? 'error' : undefined}
              />
              {error && <span {...stylex.props(styles.errorText)}>{error}</span>}
            </div>

            <Button variant="accent" size="small" onClick={handleCreateClick}>
              Создать
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('modal') as Element
  );
};
